import { addDays, nightsBetween } from './calendarDates';

/** What the host's calendar knows about one day (a subset of HostCalendarDayDTO). */
export interface CalendarDay {
  status?: string;
  /** True when Unpaqd booked the date; false when it was booked elsewhere (Airbnb, an iCal-synced platform) or blocked. */
  bysBooking?: boolean;
}

/** Booking statuses that hold their dates. A cancelled, failed, expired (also PAYMENT_LINK_EXPIRED) or never-paid booking does not. */
const DATE_HOLDING_STATUSES = new Set([
  'SUCCESS',
  'PARTIALLY_PAID',
  'PARTIAL_PAYMENT_INITIATED',
  'REMAINING_PAYMENT_INITIATED',
]);

/** A booking whose payment is still going on: it holds no dates yet, but the calendar may already show its nights as booked. */
const PAYMENT_PENDING_STATUSES = new Set(['IN_PROGRESS', 'PAYMENT_INITIATED', 'CREATED']);

export const isPaymentPending = (status: unknown): boolean => PAYMENT_PENDING_STATUSES.has(String(status));

/** Whether a booking from the host's booking list holds its dates (a missing status is taken as holding them). */
export const holdsDates = (status: unknown): boolean =>
  status == null || status === '' || DATE_HOLDING_STATUSES.has(String(status));

/**
 * Who holds a booked night:
 *  - 'unpaqd': a booking of ours (shown with the guest's name and price),
 *  - 'other': booked on another platform (only "booked, details unavailable" is shown),
 *  - 'unknown': the host's bookings could not be loaded, so it cannot be said.
 */
export type BookedNightKind = 'unpaqd' | 'other' | 'unknown';

export const classifyBookedNight = (
  day: CalendarDay | undefined,
  unpaqdBookings: unknown[],
  bookingsLoaded: boolean,
): BookedNightKind => {
  if (unpaqdBookings.length > 0) return 'unpaqd';
  if (day?.bysBooking === true) return 'unpaqd';
  if (day?.bysBooking === false) return 'other';
  // an older backend does not say: with the host's bookings loaded and none for this night, it was booked elsewhere
  return bookingsLoaded ? 'other' : 'unknown';
};

/**
 * The run of booked nights (booked elsewhere) around a date: its first and last night and how many nights it is.
 * Nights Unpaqd booked are not part of it, they have their own details.
 */
export const bookedStretch = (
  dateStr: string,
  days: Record<string, CalendarDay | undefined>,
): { from: string; to: string; nights: number } => {
  const isOtherBooked = (d: string): boolean => days[d]?.status === 'BOOKED' && days[d]?.bysBooking !== true;
  let from = dateStr;
  let to = dateStr;
  while (isOtherBooked(addDays(from, -1))) from = addDays(from, -1);
  while (isOtherBooked(addDays(to, 1))) to = addDays(to, 1);
  const nights = nightsBetween(from, to) + 1;
  return { from, to, nights };
};
